import React, { useCallback } from "react";
import { CouponInfo } from "@swiggy-private/core-ui";

// utils
import { isEmpty } from "@swiggy-private/js-utils";

// dls
import { HalfCard } from "@swiggy-private/dls-web-core/dist/components/HalfCard";
import { SuperButton } from "@swiggy-private/dls-web-core/dist/components/SuperButton";
import { CouponCard, CouponCardType } from "@swiggy-private/dls-web-recipe-common-web/dist/components/CouponCard";

// styles
import { Container, CouponCardContainer, TncBulletPointItem, StyledTitle } from "./styles";

// constants
import { TNC_HALF_CARD_TITLE, TNC_HALF_CARD_CTA_TEXT } from "./constants";

// test-ids
import {
    COUPON_TNC_HALF_CARD_CONTAINER_TEST_ID,
    COUPON_TNC_HALF_CARD_COUPON_CARD_CONTAINER_TEST_ID,
    COUPON_TNC_HALF_CARD_BULLET_POINT_ITEM_TEST_ID,
    COUPON_TNC_HALF_CARD_CTA_BUTTON_TEST_ID,
} from "@swiggy-private/cart-test-ids";

export interface TncHalfcardContentProps extends TncHalfcardProps {
    close: VoidFunction;
}

export interface TncHalfcardProps {
    couponData?: CouponInfo;
    handleClose: VoidFunction;
}

const CouponTncHalfcardContent = ({ close, couponData }: TncHalfcardContentProps) => {
    const { tnc, title: couponTitle = "", subtitle, description, logo, applicable = false, isCouponLessCard = false } =
        couponData || {};
    const { title = TNC_HALF_CARD_TITLE, bulletTexts = [] } = tnc || {};

    const handleCtaClick = useCallback(() => {
        close();
    }, [close]);

    return (
        <Container data-testid={COUPON_TNC_HALF_CARD_CONTAINER_TEST_ID}>
            <StyledTitle variant="V2.heading2.neutral.bold" color="text_High_Emphasis">
                {title}
            </StyledTitle>

            <CouponCardContainer data-testid={COUPON_TNC_HALF_CARD_COUPON_CARD_CONTAINER_TEST_ID}>
                <CouponCard
                    couponName={isCouponLessCard ? "" : couponTitle}
                    savingsTitle={subtitle}
                    description={description}
                    logoImageId={logo}
                    couponCardType={CouponCardType.PAYMENT_COUPON_CARD}
                    isTncFlow
                    applicable={applicable}
                    showApplyCta // this is used for coupon name in PaymentCouponCard
                    showRibbon={false}
                    // eslint-disable-next-line @typescript-eslint/no-empty-function
                    onApplyCouponClick={() => {}}
                />
            </CouponCardContainer>

            {!isEmpty(bulletTexts) && (
                <ul>
                    {bulletTexts.map(text => (
                        <TncBulletPointItem
                            data-testid={`${COUPON_TNC_HALF_CARD_BULLET_POINT_ITEM_TEST_ID}-${text}`}
                            key={text}
                        >
                            {text}
                        </TncBulletPointItem>
                    ))}
                </ul>
            )}

            <SuperButton
                data-testid={COUPON_TNC_HALF_CARD_CTA_BUTTON_TEST_ID}
                variant="fullwidth"
                buttonSize="large"
                buttonType="primary"
                onClick={handleCtaClick}
            >
                {TNC_HALF_CARD_CTA_TEXT}
            </SuperButton>
        </Container>
    );
};

const CouponTncHalfcard = ({ couponData, handleClose }: TncHalfcardProps) => {
    const handleBottomSheetOnClose = useCallback(() => {
        handleClose();
    }, [handleClose]);

    return (
        <HalfCard hasCustomPadding isOpen onClose={handleBottomSheetOnClose}>
            <CouponTncHalfcardContent
                close={handleBottomSheetOnClose}
                couponData={couponData}
                handleClose={handleClose}
            />
        </HalfCard>
    );
};

export default React.memo(CouponTncHalfcard);
